import type { CSSProperties } from 'react'
import type { MarginGuideEdge } from '@/components/app/document/overlays/margins-overlay'
import { PRINT_BLEED_TINT_INSIDE_TRIM_PT } from '@/lib/document/print-bleed'
import { BLEED_TINT_FILL } from '../constants/bleed-tint-fill'

type InnerTrimBleedFrameProps = {
  trimLeft: number
  trimTop: number
  trimWidth: number
  trimHeight: number
  visible?: boolean
  /**
   * Above page fill; below layout grid (z-10). Gutter yellow in `PrintBleedTintOverlay` when shown.
   */
  zIndexBase?: number
  /** Omit to draw all four in-trim edges. Use e.g. spine-only under `ShapeManager`. */
  edges?: MarginGuideEdge[]
}

/**
 * In-trim band (`PRINT_BLEED_TINT_INSIDE_TRIM_PT` from each trim edge), same fill as the outer bleed band;
 * redrawn above page fill where `PrintBleedTints` is covered by the live page.
 */
export function InnerTrimBleedFrame({
  trimLeft,
  trimTop,
  trimWidth,
  trimHeight,
  visible = true,
  zIndexBase = 7,
  edges,
}: InnerTrimBleedFrameProps) {
  if (!visible || trimWidth <= 0 || trimHeight <= 0) {
    return null
  }

  const insideTint = PRINT_BLEED_TINT_INSIDE_TRIM_PT

  if (trimWidth < insideTint * 2 || trimHeight < insideTint * 2) {
    return null
  }

  const innerH = trimHeight - 2 * insideTint
  const base: CSSProperties = {
    position: 'absolute',
    pointerEvents: 'none',
    ...BLEED_TINT_FILL,
    zIndex: zIndexBase,
  }

  const allEdges: MarginGuideEdge[] = ['top', 'bottom', 'left', 'right']
  const active = edges ?? allEdges
  const show = (e: MarginGuideEdge) => active.includes(e)

  return (
    <>
      {show('top') && (
        <div
          style={{ ...base, left: trimLeft, top: trimTop, width: trimWidth, height: insideTint }}
        />
      )}
      {show('bottom') && (
        <div
          style={{
            ...base,
            left: trimLeft,
            top: trimTop + trimHeight - insideTint,
            width: trimWidth,
            height: insideTint,
          }}
        />
      )}
      {show('left') && (
        <div
          style={{
            ...base,
            left: trimLeft,
            top: trimTop + insideTint,
            width: insideTint,
            height: innerH,
          }}
        />
      )}
      {show('right') && (
        <div
          style={{
            ...base,
            left: trimLeft + trimWidth - insideTint,
            top: trimTop + insideTint,
            width: insideTint,
            height: innerH,
          }}
        />
      )}
    </>
  )
}
